import React, { createContext, useContext, useEffect, useState } from 'react';

const colorThemes = {
  purple: {
    name: 'Purple Elegance',
    primary: {
      50: '#faf5ff',
      100: '#f3e8ff',
      200: '#e9d5ff',
      300: '#d8b4fe',
      400: '#c084fc',
      500: '#a855f7',
      600: '#9333ea',
      700: '#7e22ce',
      800: '#6b21a8',
      900: '#581c87',
    },
    secondary: {
      50: '#eef2ff',
      100: '#e0e7ff',
      200: '#c7d2fe',
      300: '#a5b4fc',
      400: '#818cf8',
      500: '#6366f1',
      600: '#4f46e5',
      700: '#4338ca',
      800: '#3730a3',
      900: '#312e81',
    },
    gradient: 'from-purple-600 to-indigo-600',
    gradientHover: 'from-purple-700 to-indigo-700',
    bgGradient: 'from-slate-50 via-purple-50 to-indigo-50',
    text: 'text-purple-600',
    bg: 'bg-purple-600',
    bgHover: 'bg-purple-700',
    border: 'border-purple-600',
    ring: 'ring-purple-500',
  },
  blue: {
    name: 'Ocean Blue',
    primary: {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a',
    },
    secondary: {
      50: '#ecfeff',
      100: '#cffafe',
      200: '#a5f3fc',
      300: '#67e8f9',
      400: '#22d3ee',
      500: '#06b6d4',
      600: '#0891b2',
      700: '#0e7490',
      800: '#155e75',
      900: '#164e63',
    },
    gradient: 'from-blue-600 to-cyan-600',
    gradientHover: 'from-blue-700 to-cyan-700',
    bgGradient: 'from-slate-50 via-blue-50 to-cyan-50',
    text: 'text-blue-600',
    bg: 'bg-blue-600',
    bgHover: 'bg-blue-700',
    border: 'border-blue-600',
    ring: 'ring-blue-500',
  },
  green: {
    name: 'Forest Green',
    primary: {
      50: '#f0fdf4',
      100: '#dcfce7',
      200: '#bbf7d0',
      300: '#86efac',
      400: '#4ade80',
      500: '#22c55e',
      600: '#16a34a',
      700: '#15803d',
      800: '#166534',
      900: '#14532d',
    },
    secondary: {
      50: '#f0fdfa',
      100: '#ccfbf1',
      200: '#99f6e4',
      300: '#5eead4',
      400: '#2dd4bf',
      500: '#14b8a6',
      600: '#0d9488',
      700: '#0f766e',
      800: '#115e59',
      900: '#134e4a',
    },
    gradient: 'from-green-600 to-teal-600',
    gradientHover: 'from-green-700 to-teal-700',
    bgGradient: 'from-slate-50 via-green-50 to-teal-50',
    text: 'text-green-600',
    bg: 'bg-green-600',
    bgHover: 'bg-green-700',
    border: 'border-green-600',
    ring: 'ring-green-500',
  },
  red: {
    name: 'Ruby Red',
    primary: {
      50: '#fef2f2',
      100: '#fee2e2',
      200: '#fecaca',
      300: '#fca5a5',
      400: '#f87171',
      500: '#ef4444',
      600: '#dc2626',
      700: '#b91c1c',
      800: '#991b1b',
      900: '#7f1d1d',
    },
    secondary: {
      50: '#fff1f2',
      100: '#ffe4e6',
      200: '#fecdd3',
      300: '#fda4af',
      400: '#fb7185',
      500: '#f43f5e',
      600: '#e11d48',
      700: '#be123c',
      800: '#9f1239',
      900: '#881337',
    },
    gradient: 'from-red-600 to-pink-600',
    gradientHover: 'from-red-700 to-pink-700',
    bgGradient: 'from-slate-50 via-red-50 to-pink-50',
    text: 'text-red-600',
    bg: 'bg-red-600',
    bgHover: 'bg-red-700',
    border: 'border-red-600',
    ring: 'ring-red-500',
  },
  orange: {
    name: 'Sunset Orange',
    primary: {
      50: '#fff7ed',
      100: '#ffedd5',
      200: '#fed7aa',
      300: '#fdba74',
      400: '#fb923c',
      500: '#f97316',
      600: '#ea580c',
      700: '#c2410c',
      800: '#9a3412',
      900: '#7c2d12',
    },
    secondary: {
      50: '#fefce8',
      100: '#fef9c3',
      200: '#fef08a',
      300: '#fde047',
      400: '#facc15',
      500: '#eab308',
      600: '#ca8a04',
      700: '#a16207',
      800: '#854d0e',
      900: '#713f12',
    },
    gradient: 'from-orange-600 to-amber-600',
    gradientHover: 'from-orange-700 to-amber-700',
    bgGradient: 'from-slate-50 via-orange-50 to-amber-50',
    text: 'text-orange-600',
    bg: 'bg-orange-600',
    bgHover: 'bg-orange-700',
    border: 'border-orange-600',
    ring: 'ring-orange-500',
  },
  slate: {
    name: 'Professional Slate',
    primary: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    },
    secondary: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
    },
    gradient: 'from-slate-700 to-slate-800',
    gradientHover: 'from-slate-800 to-slate-900',
    bgGradient: 'from-slate-50 via-slate-100 to-slate-200',
    text: 'text-slate-700',
    bg: 'bg-slate-700',
    bgHover: 'bg-slate-800',
    border: 'border-slate-700',
    ring: 'ring-slate-500',
  },
};

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  // read initial value from localStorage
  const [currentTheme, setCurrentTheme] = useState(() => {
    if (typeof window === 'undefined') return 'purple';
    return localStorage.getItem('patrolVisitTheme') || 'purple';
  });

  const theme = colorThemes[currentTheme];

  const changeTheme = (themeName) => {
    setCurrentTheme(themeName);
  };

  // write to localStorage whenever theme changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('patrolVisitTheme', currentTheme);
    }
  }, [currentTheme]);

  return (
    <ThemeContext.Provider
      value={{ theme, currentTheme, changeTheme, availableThemes: colorThemes }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};